import times from "lodash/times";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import { useEffect, useState } from "react";

import {
  Box,
  Button,
  FiAlertTriangle,
  useDisclosure,
  SimpleGrid,
  FiEdit2,
  FiTrash,
  HTMLWrapper,
  FiEdit
} from "@/storybook-components";
import ContributionCard from "../common/contribution-card";

import ActionList, { type MenuItemConfig } from "../common/action-list";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  reportIdea,
  refreshOneIdea,
  removeIdea,
  fetchIdea,
  fetchUserBudgetAllocations
} from "@/redux/features/brainstormers/thunk";
import { removeIdeaFromList } from "@/redux/features/brainstormers/slice";

import useUser from "@/hooks/useUser";
import useAuthGate from "@/hooks/useAuthGate";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import {
  setTriggerPoint,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";

import OpenPostDrawer from "../actions/open-post-drawer";
import { setTriggerPointCookie } from "@/helpers/set-trigger-point-cookie";
import { getToolLocaleKeyFromLayout } from "@/helpers/get-tool-locale-key-from-layout";
import { toSentenceCase } from "@/helpers/to-sentence-case";

const ListView = ({
  ideaList,
  lastItemRef,
  loading,
  contentToLoad,
  fetchSelectedIdea,
  manageVotes,
  manageBudgeting,
  allowUnverifiedParticipation,
  canContribute,
  projectVisibility,
  isVotingAllowed,
  brainStormerState,
  projectState
}): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const editPostDisclosure = useDisclosure();
  const createDisclosure = useDisclosure();

  const { isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn, isUserAdmin } =
    useUser();
  const {
    isGuestUser,
    isGuestParticipationEnabled,
    openGuestLoginModal,
    requireProfileCompletion
  } = useAuthGate();
  const { unverifiedUser, registeredUser } = useAppSelector(
    (state) => state.user
  );
  const {
    status: brainstormerStatus,
    meta,
    drawerStatus,
    idea,
    brainstormer
  } = useAppSelector((state) => state?.brainstormers);
  const { layout, toolConfigurationAttributes } = brainstormer;
  const contributionName =
    toolConfigurationAttributes?.preferences?.contributionName || "";

  const { count: ideasCount } = meta;

  const [isEditTriggered, setIsEditTriggered] = useState(
    brainstormerStatus === "editTriggered"
  );

  const [currentPostData, setCurrentPostData] = useState(null);
  useEffect(() => {
    if (
      brainstormerStatus === "ideaUpdated" &&
      (currentPostData?.id || idea?.id)
    ) {
      dispatch(refreshOneIdea({ ideaId: currentPostData?.id || idea?.id }));
    }
    if (brainstormerStatus === "editTriggered") {
      setIsEditTriggered(true);
    }
  }, [brainstormerStatus]);

  useEffect(() => {
    if (drawerStatus === "update" && isEditTriggered) {
      setTimeout(() => {
        editPostDisclosure.onOpen();
      }, 500);
    }
  }, [drawerStatus, isEditTriggered]);

  const { visibilityMode, id: projectId } = useAppSelector(
    (state) => state.project.project
  );
  const { id: brainstormerId, allowCommentsForIdeas } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );
  const { moderated } = useAppSelector((state) => state?.site?.site);

  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const guestParticipationAllowed =
    isGuestParticipationEnabled && allowUnverifiedParticipation;

  const newIdeaTriggerPayloadExtras = () => ({
    projectId: projectId != null ? String(projectId) : undefined,
    toolId: brainstormerId != null ? String(brainstormerId) : undefined
  });

  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }

  const actionButtonVisibility =
    projectState === "archived" ||
    brainStormerState === "archived" ||
    isVotingAllowed ||
    !(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn);

  const isActionButtonVisible =
    (visibilityMode === "Panelled" || visibilityMode === "Protected") &&
    !canContribute;

  const registeredUserLogin =
    isRegisteredUserLoggedIn && registeredUser?.profile.login;
  const unverifiedUserLogin = isRegisteredUserLoggedIn && unverifiedUser?.login;
  const userLogin = registeredUserLogin || unverifiedUserLogin;

  const handleReportClick = (currentData) => {
    const { id } = currentData;
    dispatch(
      reportIdea({
        ideaId: id
      })
    );
  };

  const handleEditClick = (currentData) => {
    dispatch(
      fetchIdea({
        projectId,
        brainstormerId,
        ideaId: currentData.id
      })
    ).then((data) => {
      setCurrentPostData(Object.assign(data?.payload, {}));
      setIsEditTriggered(true);
    });
    editPostDisclosure.onOpen();
  };

  const handleDeleteClick = async (currentData) => {
    const { id } = currentData;
    await dispatch(
      removeIdea({
        projectId,
        brainstormerId,
        ideaId: id
      })
    );
    // Remove from local state immediately
    dispatch(removeIdeaFromList(id));
    if (layout === "BudgetAllocator")
      await dispatch(fetchUserBudgetAllocations());
  };

  const menuList: MenuItemConfig[] = [
    {
      icon: <FiAlertTriangle />,
      title: translate("brainstormers.reportToModerator"),
      type: "report",
      isItemVisible: moderated,
      actionHandler: null,
      hasAlert: true,
      loggedInUser: userLogin,
      menuItemColor: "",
      menuListLabel: translate("brainstormers.reportToModerator"),
      alertContent: {
        header: translate("brainstormers.youWantToReportContent"),
        body: translate("brainstormers.reportContentDesc"),
        buttonPrimary: translate("common.yes"),
        buttonSecondary: translate("common.no"),
        buttonPrimaryVariant: "primary",
        primaryDestructive: false,
        buttonSecondaryVariant: "secondary",
        secondaryDestructive: false,
        onPrimaryClickHandler: handleReportClick
      }
    },
    {
      icon: <FiEdit2 />,
      title: translate("common.edit"),
      type: "edit",
      isItemVisible: !actionButtonVisibility && !isActionButtonVisible,
      actionHandler: handleEditClick,
      hasAlert: false,
      loggedInUser: userLogin,
      menuItemColor: "",
      menuListLabel: translate("common.edit"),
      alertContent: null,
      disabled: isActionButtonVisible
    },
    {
      icon: <FiTrash />,
      title: translate("common.delete"),
      type: "delete",
      isItemVisible: !actionButtonVisibility && !isActionButtonVisible,
      hasAlert: true,
      loggedInUser: userLogin,
      menuItemColor: "red.600",
      menuListLabel: translate("common.delete"),
      alertContent: {
        header: translate("brainstormers.ideaDeleteContentTitle"),
        body: translate("brainstormers.ideaDeleteContentDescription"),
        buttonPrimary: translate("common.delete"),
        buttonSecondary: translate("common.cancel"),
        buttonPrimaryVariant: "primary",
        primaryDestructive: true,
        buttonSecondaryVariant: "secondary",
        secondaryDestructive: false,
        onPrimaryClickHandler: handleDeleteClick
      }
    }
  ];

  const isButtonDisabled =
    ((projectVisibility === "Panelled" || projectVisibility === "Protected") &&
      !canContribute) ||
    isVotingAllowed;

  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed && isEditTriggered) {
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      if (
        triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea"
      ) {
        createDisclosure.onOpen();
        addQueryParam();
      }
      Cookies.remove("trigger_point");
    }
  });
  const haveYourSayClickHandler = () => {
    if (isButtonDisabled) return;

    if (isGuestUser) {
      if (!allowUnverifiedParticipation) {
        if (!isGuestParticipationEnabled) {
          dispatch(setModalStatus("create"));
          const activeNewIdeaTrigger = {
            triggeredSource: "new_idea",
            isTriggeredPointActive: true,
            ...newIdeaTriggerPayloadExtras()
          };
          setTriggerPointCookie(activeNewIdeaTrigger);
          dispatch(setTriggerPoint(activeNewIdeaTrigger));
          return;
        }

        const inactiveGate = {
          triggeredSource: "new_idea",
          isTriggeredPointActive: false,
          pendingProfileGate: true,
          ...newIdeaTriggerPayloadExtras()
        };
        setTriggerPointCookie(inactiveGate);
        dispatch(setTriggerPoint(inactiveGate));
        dispatch(setModalStatus("create"));
        requireProfileCompletion({
          reason: "profileGate.requiredForContribution",
          source: "ideas"
        });
        return;
      }

      createDisclosure.onOpen();
      addQueryParam();
      return;
    }

    // If user is not registered, redirect to login modal
    if (!isUnverfiedUserLoggedIn && !isRegisteredUserLoggedIn) {
      dispatch(setModalStatus("create"));
      if (guestParticipationAllowed) {
        const inactiveNewIdeaTrigger = {
          triggeredSource: "new_idea",
          isTriggeredPointActive: false,
          ...newIdeaTriggerPayloadExtras()
        };
        setTriggerPointCookie(inactiveNewIdeaTrigger);
        dispatch(setTriggerPoint(inactiveNewIdeaTrigger));
        openGuestLoginModal({ action: "new_idea" });
      } else {
        const activeNewIdeaTrigger = {
          triggeredSource: "new_idea",
          isTriggeredPointActive: true,
          ...newIdeaTriggerPayloadExtras()
        };
        setTriggerPointCookie(activeNewIdeaTrigger);
        dispatch(setTriggerPoint(activeNewIdeaTrigger));
      }
      return;
    }

    createDisclosure.onOpen();
    addQueryParam();
  };

  const addQueryParam = () => {
    router.query.action = "new_idea";
    router.replace(router, "", { shallow: true });
  };

  return (
    <Box mt={12} className="ehq-ideas-list" data-testid="ideas-list">
      {ideasCount > 0 && ideaList.length > 0 ? (
        <SimpleGrid
          className="ideas-search-node"
          css={{ mark: { bg: "yellow.300" } }}
          justifyItems="center"
          templateColumns="repeat(auto-fill,minmax(18rem,1fr))"
          gap={14}
          data-testid="ideas-grid"
        >
          {ideaList.map((datum, index) => {
            return (
              <Box
                minW="18rem"
                maxW="28rem"
                w="100%"
                key={datum.id}
                ref={ideaList.length === index + 1 ? lastItemRef : null}
                data-testid={`ideas-card-${datum.id}`}
              >
                <ContributionCard
                  key={datum.id}
                  cardData={datum}
                  clickHandler={() => fetchSelectedIdea(datum.id)}
                  cardLoaded
                  actionList={
                    <ActionList menuList={menuList} currentData={datum} />
                  }
                  manageVotes={manageVotes}
                  manageBudgeting={manageBudgeting}
                  handleComment={() => fetchSelectedIdea(datum.id)}
                  showCommentIcon={allowCommentsForIdeas}
                />
              </Box>
            );
          })}
          {loading &&
            times(contentToLoad, (index) => {
              return (
                <Box
                  minW="18rem"
                  maxW="28rem"
                  w="100%"
                  key={index}
                  data-testid={`ideas-card-skeleton-${index}`}
                >
                  <ContributionCard
                    key={index}
                    cardData={null}
                    clickHandler={null}
                    cardLoaded={false}
                    actionList={null}
                    manageVotes={null}
                    manageBudgeting={null}
                    handleComment={null}
                  />
                </Box>
              );
            })}
        </SimpleGrid>
      ) : ideasCount !== null && ideaList.length === 0 ? (
        projectState !== "archived" &&
        brainStormerState !== "archived" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            pt={20}
            gap={4}
            data-testid="ideas-empty-state"
          >
            <HTMLWrapper
              html={translate("brainstormers.noContentText")}
              textAlign="center"
              as="p"
              data-testid="ideas-empty-message"
            />
            {(layout !== "BudgetAllocator" || isUserAdmin) && (
              <Button
                variant="primary"
                leftIcon={<FiEdit />}
                fontWeight="semibold"
                fontSize="md"
                onClick={haveYourSayClickHandler}
                disabled={isButtonDisabled}
                size="lg"
                width={{ base: "full", lg: "auto" }}
                px={6}
                py={3}
                data-testid="ideas-new-contribution-button"
              >
                {toSentenceCase(
                  translate("tools.newContribution", {
                    contributionName:
                      contributionName.trim() ||
                      translate(
                        `tools.${getToolLocaleKeyFromLayout(layout)}.callToAction`
                      )
                  })
                )}
              </Button>
            )}
          </Box>
        )
      ) : (
        <SimpleGrid
          justifyItems="center"
          templateColumns="repeat(auto-fill,minmax(18rem,1fr))"
          gap={14}
          data-testid="ideas-grid-skeleton"
        >
          {times(20, (index) => {
            return (
              <Box
                minW="18rem"
                maxW="28rem"
                w="100%"
                key={index}
                data-testid={`ideas-card-skeleton-${index}`}
              >
                <ContributionCard
                  key={index}
                  cardData={null}
                  clickHandler={null}
                  cardLoaded={false}
                  actionList={null}
                  manageVotes={null}
                  handleComment={null}
                  manageBudgeting={null}
                />
              </Box>
            );
          })}
        </SimpleGrid>
      )}
      {(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn) &&
        isEditTriggered && (
          <OpenPostDrawer
            onOpen={editPostDisclosure.onOpen}
            open={editPostDisclosure.open}
            onClose={editPostDisclosure.onClose}
            type="edit"
            isEditPost={isEditTriggered}
            editHandler={setIsEditTriggered}
            currentPostData={idea || currentPostData}
          />
        )}
    </Box>
  );
};

export default ListView;
